import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Snowflake, MapPin, User, Phone, Loader2,
  Plus, Minus, Waves, Navigation,
  CreditCard, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IceDeliveryPage = () => {
  const { t, language } = useLanguage();
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cart, setCart] = useState({});
  const [platformStatus, setPlatformStatus] = useState(null);

  const [formData, setFormData] = useState({
    delivery_location: '',
    location_type: 'beach',
    gps_coordinates: '',
    beach_description: '',
    customer_name: user?.name || '',
    phone: user?.phone || '',
    payment_method: 'card',
    notes: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, settingsRes, cfgRes] = await Promise.all([
          fetch(`${API_URL}/api/ice-products`),
          fetch(`${API_URL}/api/settings`),
          fetch(`${API_URL}/api/config/public`)
        ]);

        if (productsRes.ok) {
          const data = await productsRes.json();
          setProducts(data);
        }

        if (settingsRes.ok) {
          const data = await settingsRes.json();
          setSettings(data);
        }

        if (cfgRes.ok) {
          setPlatformStatus(await cfgRes.json());
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Auto-fill user data when logged in
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || prev.customer_name,
        phone: user.phone || prev.phone,
      }));
    }
  }, [user]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateCart = (productId, delta) => {
    setCart(prev => {
      const current = prev[productId] || 0;
      const newQty = Math.max(0, current + delta);
      if (newQty === 0) {
        const { [productId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [productId]: newQty };
    });
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = `${position.coords.latitude}, ${position.coords.longitude}`;
          setFormData(prev => ({ ...prev, gps_coordinates: coords }));
          toast.success(language === 'pt' ? 'Localização obtida!' : 'Location obtained!');
        },
        (error) => {
          toast.error(language === 'pt' ? 'Erro ao obter localização' : 'Error getting location');
        }
      );
    }
  };

  const calculatePrices = () => {
    const subtotal = Object.entries(cart).reduce((sum, [productId, qty]) => {
      const product = products.find(p => p.product_id === productId);
      return sum + (product ? product.price * qty : 0);
    }, 0);

    const deliveryFee = formData.location_type === 'beach' 
      ? (settings?.beach_delivery_fee || 5.0) 
      : (settings?.standard_delivery_fee || 2.5);

    return { subtotal, deliveryFee, total: subtotal + deliveryFee };
  };

  const prices = calculatePrices();
  const hasItems = Object.keys(cart).length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasItems) {
      toast.error(language === 'pt' ? 'Adicione produtos ao carrinho' : 'Add products to cart');
      return;
    }

    setSubmitting(true);

    try {
      const items = Object.entries(cart).map(([productId, qty]) => {
        const product = products.find(p => p.product_id === productId);
        return {
          product_id: productId,
          name: language === 'pt' ? product.name_pt : product.name,
          price: product.price,
          quantity: qty
        };
      });

      const orderData = {
        items,
        customer_name: formData.customer_name,
        phone: formData.phone,
        delivery_location: formData.delivery_location,
        location_type: formData.location_type,
        gps_coordinates: formData.gps_coordinates,
        beach_description: formData.beach_description,
        payment_method: formData.payment_method,
        notes: formData.notes,
        is_beach_mode: formData.location_type === 'beach',
        delivery_fee: prices.deliveryFee,
        subtotal: prices.subtotal,
        total: prices.total
      };

      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/api/ice-orders`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const detail = typeof errData.detail === 'string' ? errData.detail : null;
        throw new Error(detail || (language === 'pt' ? 'Não foi possível criar o pedido' : 'Could not create the order'));
      }

      const order = await response.json();

      // If paying with card, redirect to Stripe
      if (formData.payment_method === 'card') {
        const checkoutResponse = await fetch(`${API_URL}/api/payments/create-checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            origin_url: window.location.origin,
            order_id: order.order_id,
            amount: prices.total,
            order_type: 'ice'
          })
        });

        if (!checkoutResponse.ok) {
          throw new Error('Failed to create payment session');
        }

        const checkoutData = await checkoutResponse.json();
        window.location.href = checkoutData.url;
        return;
      }

      // Cash
      try {
        const { saveActiveOrder } = await import('../utils/activeOrder');
        saveActiveOrder(order.order_id, 'ice');
      } catch (e) { /* ignore */ }
      toast.success(
        language === 'pt' ? 'Pedido de gelo realizado! Pague na entrega.' : 'Ice order placed! Pay on delivery.'
      );
      navigate(`/order-tracking/${order.order_id}`);

    } catch (error) {
      console.error('Order error:', error);
      toast.error(
        error?.message || (language === 'pt' ? 'Erro ao processar pedido' : 'Error processing order'),
        { duration: 8000 }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const locationTypes = [
    { id: 'beach', label: language === 'pt' ? 'Praia' : 'Beach' },
    { id: 'hotel', label: 'Hotel' },
    { id: 'apartment', label: language === 'pt' ? 'Apartamento' : 'Apartment' },
    { id: 'marina', label: 'Marina' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] py-8">
        <div className="container mx-auto px-4">
          <Skeleton className="h-12 w-64 mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Skeleton className="h-64 rounded-3xl" />
            </div>
            <Skeleton className="h-96 rounded-3xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Hero */}
      <div 
        className="relative h-48 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#004E64]/60 to-[#004E64]/80" />
        <div className="relative container mx-auto px-4 h-full flex items-center">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Snowflake className="text-white" size={28} />
              <span className="font-handwritten text-xl text-white/90">Beach Essential</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white" data-testid="ice-title">
              {language === 'pt' ? 'Entrega de Gelo' : 'Ice Delivery'}
            </h1>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {platformStatus && platformStatus.platform_is_open === false && (
          <div className="mb-6 flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3" data-testid="ice-closed-banner">
            <Snowflake size={18} className="mt-0.5 shrink-0 text-amber-600" />
            <div>
              <p className="font-semibold text-amber-900 text-sm">
                {language === 'pt' ? 'Fechado de momento' : 'Currently closed'}
              </p>
              <p className="text-amber-800 text-xs mt-0.5">
                {language === 'pt'
                  ? `A plataforma está fechada neste momento.${platformStatus.platform_hours?.is_enabled ? ` Horário: ${platformStatus.platform_hours?.open}\u2013${platformStatus.platform_hours?.close}.` : ''} Não é possível concluir pedidos.`
                  : `The platform is closed right now. Orders can't be completed.`}
              </p>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Products */}
            <div className="lg:col-span-2 space-y-6">
              {/* Product Selection */}
              <Card className="rounded-3xl border-[#004E64]/5">
                <CardHeader>
                  <CardTitle className="text-[#004E64]">
                    {language === 'pt' ? 'Selecione os Produtos' : 'Select Products'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {products.map(product => {
                      const qty = cart[product.product_id] || 0;
                      const unavailable = product.is_available === false;
                      return (
                        <div
                          key={product.product_id}
                          className={`rounded-2xl p-4 border-2 transition-all ${
                            unavailable ? 'border-[#004E64]/10 opacity-50 grayscale' :
                            qty > 0 ? 'border-[#2EC4B6] bg-[#2EC4B6]/5' : 'border-[#004E64]/10'
                          }`}
                        >
                          <div className="flex gap-3">
                            {product.image && (
                              <img 
                                src={product.image} 
                                alt={product.name}
                                className="w-16 h-16 rounded-xl object-cover"
                              />
                            )}
                            <div className="flex-1">
                              <h4 className="font-bold text-[#004E64] flex items-center gap-2">
                                {language === 'pt' ? product.name_pt : product.name}
                                {unavailable && (
                                  <span className="text-xs font-semibold text-white bg-red-500 px-2 py-0.5 rounded-full" data-testid={`ice-unavailable-${product.product_id}`}>
                                    {language === 'pt' ? 'Indisponível' : 'Unavailable'}
                                  </span>
                                )}
                              </h4>
                              <p className="text-sm text-[#9FA2B4]">
                                {language === 'pt' ? product.description_pt : product.description}
                              </p>
                              <p className="text-[#FF6B35] font-bold mt-1">€{product.price.toFixed(2)}</p>
                            </div>
                          </div>
                          <div className="flex items-center justify-end gap-3 mt-3">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="w-8 h-8 rounded-full"
                              onClick={() => updateCart(product.product_id, -1)}
                              disabled={qty === 0}
                            >
                              <Minus size={14} />
                            </Button>
                            <span className="w-8 text-center font-bold text-[#004E64]">{qty}</span>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="w-8 h-8 rounded-full"
                              onClick={() => updateCart(product.product_id, 1)}
                              disabled={unavailable}
                            >
                              <Plus size={14} />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Delivery Info */}
              <Card className="rounded-3xl border-[#004E64]/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#004E64]">
                    <MapPin className="text-[#FF6B35]" />
                    {language === 'pt' ? 'Entrega na Praia' : 'Beach Delivery'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>{language === 'pt' ? 'Nome' : 'Name'} *</Label>
                      <Input
                        value={formData.customer_name}
                        onChange={(e) => handleChange('customer_name', e.target.value)}
                        required
                        className="rounded-xl mt-1"
                      />
                    </div>
                    <div>
                      <Label>{language === 'pt' ? 'Telefone' : 'Phone'} *</Label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        required
                        className="rounded-xl mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>{language === 'pt' ? 'Tipo de Local' : 'Location Type'} *</Label>
                    <Select
                      value={formData.location_type}
                      onValueChange={(v) => handleChange('location_type', v)}
                    >
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {locationTypes.map(type => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>{language === 'pt' ? 'Local de Entrega' : 'Delivery Location'} *</Label>
                    <Input
                      value={formData.delivery_location}
                      onChange={(e) => handleChange('delivery_location', e.target.value)}
                      required
                      className="rounded-xl mt-1"
                      placeholder={language === 'pt' ? 'Ex: Praia de Tróia, perto do bar' : 'Ex: Troia Beach, near the bar'}
                    />
                  </div>

                  {formData.location_type === 'beach' && (
                    <>
                      <div>
                        <Label className="flex items-center gap-2">
                          <Navigation size={14} />
                          {language === 'pt' ? 'Coordenadas GPS' : 'GPS Coordinates'}
                        </Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            value={formData.gps_coordinates}
                            onChange={(e) => handleChange('gps_coordinates', e.target.value)}
                            className="rounded-xl"
                            placeholder="latitude, longitude"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={getLocation}
                            className="rounded-xl"
                          >
                            <Navigation size={16} />
                          </Button>
                        </div>
                      </div>

                      <div>
                        <Label className="flex items-center gap-2">
                          <Waves size={14} />
                          {language === 'pt' ? 'Descrição na Praia' : 'Beach Description'}
                        </Label>
                        <Textarea
                          value={formData.beach_description}
                          onChange={(e) => handleChange('beach_description', e.target.value)}
                          className="rounded-xl mt-1"
                          placeholder={language === 'pt' 
                            ? 'Ex: Guarda-sol azul perto do posto de salvamento' 
                            : 'Ex: Blue umbrella near the lifeguard post'}
                          rows={2}
                        />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="rounded-3xl border-[#004E64]/5 sticky top-24">
                <CardHeader>
                  <CardTitle className="text-[#004E64]">
                    {language === 'pt' ? 'Resumo do Pedido' : 'Order Summary'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!hasItems ? (
                    <p className="text-[#9FA2B4] text-center py-4">
                      {language === 'pt' ? 'Adicione produtos ao carrinho' : 'Add products to cart'}
                    </p>
                  ) : (
                    <>
                      {Object.entries(cart).map(([productId, qty]) => {
                        const product = products.find(p => p.product_id === productId);
                        if (!product) return null;
                        return (
                          <div key={productId} className="flex justify-between text-sm">
                            <span className="text-[#004E64]">
                              {qty}x {language === 'pt' ? product.name_pt : product.name}
                            </span>
                            <span className="font-semibold text-[#004E64]">
                              €{(product.price * qty).toFixed(2)}
                            </span>
                          </div>
                        );
                      })}

                      <hr className="border-[#004E64]/10" />

                      <div className="flex justify-between text-[#9FA2B4]">
                        <span>{language === 'pt' ? 'Subtotal' : 'Subtotal'}</span>
                        <span>€{prices.subtotal.toFixed(2)}</span>
                      </div>

                      <div className="flex justify-between text-[#9FA2B4]">
                        <span className="flex items-center gap-2">
                          {language === 'pt' ? 'Taxa de Entrega' : 'Delivery Fee'}
                          {formData.location_type === 'beach' && <Waves size={14} className="text-[#2EC4B6]" />}
                        </span>
                        <span>€{prices.deliveryFee.toFixed(2)}</span>
                      </div>

                      <hr className="border-[#004E64]/10" />

                      <div className="flex justify-between text-lg">
                        <span className="font-bold text-[#004E64]">Total</span>
                        <span className="font-bold text-[#FF6B35]">€{prices.total.toFixed(2)}</span>
                      </div>

                      {/* Payment Method Selection */}
                      <div className="mt-2">
                        <p className="text-sm font-semibold text-[#004E64] mb-2">
                          {language === 'pt' ? 'Metodo de Pagamento' : 'Payment Method'}
                        </p>
                        <div className="space-y-2">
                          {[
                            { value: 'card', label: language === 'pt' ? 'Cartao' : 'Card', icon: CreditCard, desc: 'Visa, Mastercard' }
                          ].map(method => (
                            <button
                              key={method.value}
                              type="button"
                              onClick={() => handleChange('payment_method', method.value)}
                              className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                                formData.payment_method === method.value
                                  ? 'border-[#FF6B35] bg-[#FF6B35]/5'
                                  : 'border-[#004E64]/10 hover:border-[#FF6B35]/30'
                              }`}
                              data-testid={`payment-${method.value}`}
                            >
                              <method.icon size={20} className={formData.payment_method === method.value ? 'text-[#FF6B35]' : 'text-[#9FA2B4]'} />
                              <div className="text-left">
                                <p className={`text-sm font-semibold ${formData.payment_method === method.value ? 'text-[#FF6B35]' : 'text-[#004E64]'}`}>{method.label}</p>
                                <p className="text-xs text-[#9FA2B4]">{method.desc}</p>
                              </div>
                              {formData.payment_method === method.value && (
                                <Check size={16} className="ml-auto text-[#FF6B35]" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>

                      <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full py-6 text-lg font-bold"
                        data-testid="order-ice-button"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="mr-2 animate-spin" />
                            {language === 'pt' ? 'Processando...' : 'Processing...'}
                          </>
                        ) : (
                          language === 'pt' ? 'Pedir Gelo' : 'Order Ice'
                        )}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IceDeliveryPage;
